/**
 * WhatsNewNotice.jsx — s101 (Dr. Rami, 2026-10-12; mockup "Orella What's New Notice" approved).
 * Replaces WhatsNewSheet (the pop-up, TEST 2.20). The release notes are the FIRST item of the
 * notifications list: unread (counted in the bell, NotificationBellButton) until the patient opens
 * it, then a read item until the next release replaces it.
 *
 * ★★★ THE ONLY WRITE TO orella_whats_new_seen IS OPENING THE NOTICE — inline in onClick, never on
 * mount (scripts/test-whats-new.mjs ARM 11). Seen-state stays per device (D1, no API).
 * Chrome copy through t() (whatsNew.title, whatsNew.tapToRead); the note items are data from
 * src/release-notes.json in the app language with an English fallback (itemsForLanguage).
 * It lives only on /notifications, where the bell leads — never over /case/emergency or a check-in.
 */
import { useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import { useNotificationStore } from '../stores'
import { APP_VERSION } from '../utils/appVersion'
import releaseNotes from '../release-notes.json'
import { noticeNotes, itemsForLanguage, readLastSeenVersion, markSeenVersion } from '../utils/whatsNew'

const RUNNING_VERSION = APP_VERSION.replace(/^v/, '')

export default function WhatsNewNotice() {
  const { t, language } = useI18n()
  const setWhatsNewUnread = useNotificationStore((s) => s.setWhatsNewUnread)
  const [notice, setNotice] = useState(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setNotice(noticeNotes(releaseNotes, RUNNING_VERSION, readLastSeenVersion()))
  }, [])

  if (!notice || notice.entries.length === 0) return null
  const bullets = notice.entries.flatMap((entry) => itemsForLanguage(entry, language).map((b, i) => ({ key: `${entry.version}-${i}`, b })))
  if (bullets.length === 0) return null
  const unread = notice.unread

  return (
    <div className="px-4 pt-1 pb-2">
      <button
        type="button"
        data-whats-new={unread ? 'unread' : 'read'}
        aria-expanded={open}
        onClick={() => {
          if (unread) { markSeenVersion(RUNNING_VERSION); setNotice({ ...notice, unread: false }); setWhatsNewUnread(false) }
          setOpen((v) => !v)
        }}
        className="w-full text-left rounded-2xl px-4 py-3 border transition-colors"
        style={unread
          ? { background: '#E9F8FF', borderColor: '#46A8F9' }
          : { background: '#FFFFFF', borderColor: '#E2E8F0' }}
      >
        <span className="flex items-center gap-2">
          {unread && <span aria-hidden="true" className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#46A8F9' }} />}
          <span className="text-[14px] font-bold" style={{ color: '#15354F' }}>{t('whatsNew.title', "What's new")}</span>
        </span>
        {!open && (
          <span className="block text-[12.5px] mt-0.5" style={{ color: '#64748B' }}>
            {t('whatsNew.tapToRead', { count: bullets.length, defaultValue: '{{count}} updates in this version · tap to read' })}
          </span>
        )}
        {open && (
          <ul className="mt-2 space-y-1.5 list-disc pl-5 text-[13px]" style={{ color: '#334155' }}>
            {bullets.map(({ key, b }) => <li key={key}>{b}</li>)}
          </ul>
        )}
      </button>
    </div>
  )
}
